import { useState, useEffect, useMemo } from "react";
import { useAuth } from "../../App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Checkbox } from "../../components/ui/checkbox";
import { toast } from "sonner";
import {
  Wallet,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Send,
  Building2,
  Search,
  FileText,
  Receipt,
} from "lucide-react";

const ORIGEN_COLORS = {
  Catastro: "bg-indigo-100 text-indigo-700 border-indigo-200",
  Publicidad: "bg-purple-100 text-purple-700 border-purple-200",
  Vehiculos: "bg-sky-100 text-sky-700 border-sky-200",
  Ambulantes: "bg-teal-100 text-teal-700 border-teal-200",
  "Desarrollo Urbano": "bg-orange-100 text-orange-700 border-orange-200",
  LAE: "bg-emerald-100 text-emerald-700 border-emerald-200",
  Hacienda: "bg-slate-100 text-slate-700 border-slate-200",
};

const PortalPendientesPage = () => {
  const { API, token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [loading, setLoading] = useState(false);
  const [pendientes, setPendientes] = useState([]);
  const [selected, setSelected] = useState({});
  const [search, setSearch] = useState("");
  const [filterOrigen, setFilterOrigen] = useState("");
  const [generating, setGenerating] = useState(false);

  const fetchPendientes = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/portal/pendientes-pago`, { headers });
      setPendientes(res.data || []);
      setSelected({});
    } catch (err) {
      toast.error("Error al cargar pendientes de pago");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendientes();
  }, []);

  const origenes = useMemo(() => {
    const set = new Set(pendientes.map((p) => p.origen || "Hacienda"));
    return Array.from(set);
  }, [pendientes]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return pendientes.filter((p) => {
      if (filterOrigen && (p.origen || "Hacienda") !== filterOrigen) return false;
      if (!term) return true;
      return (
        (p.nro_planilla || "").toLowerCase().includes(term) ||
        (p.detalle_conceptos || "").toLowerCase().includes(term) ||
        (p.origen || "").toLowerCase().includes(term) ||
        (p.codigo_catastral || "").toLowerCase().includes(term)
      );
    });
  }, [pendientes, search, filterOrigen]);

  const selectables = filtered.filter((p) => !p.tiene_reporte);

  const toggleAll = () => {
    const allSelected =
      selectables.length > 0 &&
      selectables.every((p) => selected[p.id]);
    const next = { ...selected };
    selectables.forEach((p) => {
      next[p.id] = !allSelected;
    });
    setSelected(next);
  };

  const toggleOne = (id) => {
    setSelected((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const selectedList = filtered.filter((p) => selected[p.id] && !p.tiene_reporte);
  const totalSelected = selectedList.reduce((acc, p) => acc + (p.total || 0), 0);
  const totalGeneral = filtered.reduce((acc, p) => acc + (p.total || 0), 0);
  const totalConReporte = filtered.filter(p => p.tiene_reporte).length;

  const handleGenerar = async () => {
    if (selectedList.length === 0) {
      toast.error("Seleccione al menos una declaración para reportar el pago");
      return;
    }
    setGenerating(true);
    try {
      const res = await axios.post(
        `${API}/portal/reporte-pago`,
        { declaracion_ids: selectedList.map((p) => p.id) },
        { headers }
      );
      toast.success(res.data.message);
      fetchPendientes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al generar reporte");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="portal-pendientes-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Cálculos Enviados a Caja</h2>
          <p className="text-sm text-slate-500">
            Declaraciones generadas por la Alcaldía que están pendientes de pago
          </p>
        </div>
        <Button
          onClick={fetchPendientes}
          variant="outline"
          size="sm"
          data-testid="btn-refresh-pendientes"
        >
          <RefreshCw className="w-4 h-4 mr-2" /> Actualizar
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-amber-200 bg-amber-50/40">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-amber-700 font-medium">Total Pendiente</p>
                <p className="text-lg font-bold text-amber-900">
                  Bs. {totalGeneral.toLocaleString("es-VE", { minimumFractionDigits: 2 })}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Declaraciones</p>
                <p className="text-lg font-bold">{filtered.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Seleccionadas</p>
                <p className="text-lg font-bold text-emerald-700">
                  {selectedList.length}
                </p>
                <p className="text-[11px] text-slate-400">
                  Bs. {totalSelected.toLocaleString("es-VE", { minimumFractionDigits: 2 })}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-violet-100 rounded-lg">
                <Receipt className="w-5 h-5 text-violet-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Ya Reportadas</p>
                <p className="text-lg font-bold text-violet-700">
                  {totalConReporte}
                </p>
                <p className="text-[11px] text-slate-400">con recibo pendiente</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters + Action bar */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Wallet className="w-5 h-5 text-amber-600" />
              Listado de Cálculos
            </CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative w-56">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Buscar planilla, concepto..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 text-sm h-9"
                  data-testid="input-search-pendientes"
                />
              </div>
              <select
                value={filterOrigen}
                onChange={(e) => setFilterOrigen(e.target.value)}
                className="h-9 text-sm border border-slate-300 rounded-md px-2 bg-white"
                data-testid="select-filter-origen"
              >
                <option value="">Todos los orígenes</option>
                {origenes.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
              <Button
                onClick={handleGenerar}
                disabled={generating || selectedList.length === 0}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="btn-generar-reporte-seleccionados"
              >
                {generating ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Reportar pago ({selectedList.length})
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-slate-500" data-testid="no-pendientes">
              <CheckCircle className="w-14 h-14 mx-auto mb-3 text-green-300" />
              <p className="font-semibold text-green-700">No tiene cálculos pendientes</p>
              <p className="text-sm mt-1">Todas sus obligaciones municipales están al día.</p>
            </div>
          ) : (
            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-slate-50 text-slate-600">
                    <th className="w-10 py-2 px-3">
                      <Checkbox
                        checked={
                          selectables.length > 0 &&
                          selectables.every((p) => selected[p.id])
                        }
                        onCheckedChange={toggleAll}
                        data-testid="checkbox-select-all"
                      />
                    </th>
                    <th className="text-left py-2 px-3 font-medium">Nro. Planilla</th>
                    <th className="text-left py-2 px-3 font-medium">Fecha</th>
                    <th className="text-left py-2 px-3 font-medium">Origen</th>
                    <th className="text-left py-2 px-3 font-medium">Concepto</th>
                    <th className="text-right py-2 px-3 font-medium">Total (Bs.)</th>
                    <th className="text-left py-2 px-3 font-medium">Estatus</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((d) => {
                    const origenColor =
                      ORIGEN_COLORS[d.origen] || ORIGEN_COLORS.Hacienda;
                    const disabled = d.tiene_reporte;
                    return (
                      <tr
                        key={d.id}
                        className={`border-b hover:bg-slate-50 ${
                          disabled ? "opacity-70" : ""
                        }`}
                        data-testid={`pendiente-row-${d.nro_planilla}`}
                      >
                        <td className="py-2 px-3">
                          <Checkbox
                            checked={!!selected[d.id]}
                            onCheckedChange={() => !disabled && toggleOne(d.id)}
                            disabled={disabled}
                            data-testid={`checkbox-${d.nro_planilla}`}
                          />
                        </td>
                        <td className="py-2 px-3 font-mono font-medium">
                          {d.nro_planilla}
                        </td>
                        <td className="py-2 px-3 text-slate-600">{d.fecha}</td>
                        <td className="py-2 px-3">
                          <Badge className={`${origenColor} border gap-1`}>
                            <Building2 className="w-3 h-3" />
                            {d.origen || "Hacienda"}
                          </Badge>
                        </td>
                        <td className="py-2 px-3 text-slate-600 max-w-xs truncate">
                          {d.detalle_conceptos || (
                            <span className="text-slate-400 italic">Sin detalle</span>
                          )}
                          {d.codigo_catastral && (
                            <div className="text-[11px] text-slate-400 font-mono">
                              Cat. {d.codigo_catastral}
                            </div>
                          )}
                        </td>
                        <td className="py-2 px-3 text-right font-medium text-slate-800">
                          {(d.total || 0).toLocaleString("es-VE", {
                            minimumFractionDigits: 2,
                          })}
                        </td>
                        <td className="py-2 px-3">
                          {d.tiene_reporte ? (
                            <div className="flex flex-col gap-0.5">
                              <Badge className="bg-violet-100 text-violet-700 border-violet-200 border w-fit">
                                Reporte {d.estatus_reporte}
                              </Badge>
                              {d.nro_recibo_generado && (
                                <span className="text-[11px] text-slate-500 font-mono">
                                  Recibo {d.nro_recibo_generado}
                                </span>
                              )}
                            </div>
                          ) : (
                            <Badge className="bg-red-100 text-red-700 border-red-200 border">
                              Sin pagar
                            </Badge>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-100 font-semibold">
                    <td colSpan={5} className="py-2 px-3 text-right">
                      Total mostrado:
                    </td>
                    <td className="py-2 px-3 text-right">
                      {totalGeneral.toLocaleString("es-VE", {
                        minimumFractionDigits: 2,
                      })}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PortalPendientesPage;
